'use client'

import * as React from 'react'
import { Search, Plus, Building2, Mail, Phone, MoreHorizontal } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'
import { cn } from 'lib/utils'
import { addDoc, collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore'
import { db } from '../../../firebase'
import { useUser } from 'components/context/user-context'

const typeConfig= {
  stakeholder: { color: 'bg-primary/15 text-primary', label: 'Stakeholder' },
  partner: { color: 'bg-secondary text-secondary-foreground', label: 'Partner' },
}

const influenceColor= { high: 'bg-destructive', medium: 'bg-warning', low: 'bg-success' }
const interestColor= { high: 'bg-primary', medium: 'bg-secondary', low: 'bg-muted' }

export default function StakeholdersPage() {
  const { canEdit } = useUser()
  const [search, setSearch] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [stakeholders, setStakeholders] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [editingId, setEditingId] = React.useState(null)
  const [form, setForm] = React.useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    interest: 'medium',
    influence: 'medium',
    type: 'stakeholder',
    role: '',
    sector: 'Community',
    location: '',
    engagementLevel: 'consult',
    preferredContact: '',
    notes: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadStakeholders() {
      try {
        const [stakeholderSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'stakeholders')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        setStakeholders(stakeholderSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setStakeholders([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadStakeholders()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const normalizedStakeholders = stakeholders.map((stakeholder) => ({
    ...stakeholder,
    name: stakeholder.name || stakeholder.fullName || 'Stakeholder',
    organization: stakeholder.organization || stakeholder.organisation || '—',
    email: stakeholder.email || '',
    phone: stakeholder.phone || '',
    interest: stakeholder.interest || 'medium',
    influence: stakeholder.influence || 'medium',
    type: stakeholder.type || 'community',
    role: stakeholder.role || stakeholder.position || '',
    sector: stakeholder.sector || stakeholder.affiliation || '',
    location: stakeholder.location || stakeholder.community || '',
    engagementLevel: stakeholder.engagementLevel || stakeholder.engagement || '',
    preferredContact: stakeholder.preferredContact || stakeholder.preferredCommunication || '',
    notes: stakeholder.notes || '',
    projectIds: Array.isArray(stakeholder.projectIds) ? stakeholder.projectIds : [],
  }))

  const filtered = normalizedStakeholders.filter((stakeholder) => {
    const ms =
      stakeholder.name.toLowerCase().includes(search.toLowerCase()) ||
      stakeholder.organization.toLowerCase().includes(search.toLowerCase())
    const mt = typeFilter === 'all' || stakeholder.type === typeFilter
    return ms && mt
  })

  const byType = Object.entries(
    normalizedStakeholders.reduce((acc, stakeholder) => {
      acc[stakeholder.type] = (acc[stakeholder.type] || 0) + 1
      return acc
    }, {}),
  )

  const updateForm = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const resetForm = () => {
    setForm({
      name: '',
      organization: '',
      email: '',
      phone: '',
      interest: 'medium',
      influence: 'medium',
      type: 'stakeholder',
      role: '',
      sector: 'Community',
      location: '',
      engagementLevel: 'consult',
      preferredContact: '',
      notes: '',
    })
  }

  const openForCreate = () => {
    setEditingId(null)
    resetForm()
    setOpen(true)
  }

  const openForEdit = (stakeholder) => {
    setEditingId(stakeholder.id)
    setForm({
      name: stakeholder.name || stakeholder.fullName || '',
      organization: stakeholder.organization || stakeholder.organisation || '',
      email: stakeholder.email || '',
      phone: stakeholder.phone || '',
      interest: stakeholder.interest || 'medium',
      influence: stakeholder.influence || 'medium',
      type: stakeholder.type || 'stakeholder',
      role: stakeholder.role || stakeholder.position || '',
      sector: stakeholder.sector || stakeholder.affiliation || 'Community',
      location: stakeholder.location || stakeholder.community || '',
      engagementLevel: stakeholder.engagementLevel || stakeholder.engagement || 'consult',
      preferredContact: stakeholder.preferredContact || stakeholder.preferredCommunication || '',
      notes: stakeholder.notes || '',
    })
    setOpen(true)
  }

  const handleSaveStakeholder = async () => {
    if (!form.name.trim()) {
      return
    }
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        organization: form.organization.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        influence: form.influence,
        type: form.type,
        role: form.role.trim(),
        sector: form.sector.trim(),
        location: form.location.trim(),
        engagementLevel: form.engagementLevel,
        preferredContact: form.preferredContact.trim(),
        notes: form.notes.trim(),
        projectIds: [],
        updatedAt: serverTimestamp(),
      }
      if (editingId) {
        await updateDoc(doc(db, 'stakeholders', editingId), payload)
        setStakeholders((prev) =>
          prev.map((item) => (item.id === editingId ? { ...item, ...payload } : item)),
        )
      } else {
        const docRef = await addDoc(collection(db, 'stakeholders'), {
          ...payload,
          createdAt: serverTimestamp(),
        })
        setStakeholders((prev) => [{ id: docRef.id, ...payload, createdAt: serverTimestamp() }, ...prev])
      }
      resetForm()
      setEditingId(null)
      setOpen(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Stakeholder Registry</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage stakeholders, their influence, interest, and engagement across projects</p>
        </div>
        {canEdit ? (
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen)
              if (!nextOpen) {
                setEditingId(null)
                resetForm()
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-accent gap-2" onClick={openForCreate}>
                <Plus className="size-4" />Add Stakeholder
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Stakeholder' : 'Add Stakeholder'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Stakeholder Name</Label>
                  <Input value={form.name} onChange={updateForm('name')} placeholder="Full name" />
                </div>
                <div className="space-y-2">
                  <Label>Organisation</Label>
                  <Input value={form.organization} onChange={updateForm('organization')} placeholder="Institution or community group" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={updateForm('email')} placeholder="email@example.com" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={updateForm('phone')} placeholder="+254..." />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Stakeholder Type</Label>
                  <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stakeholder">Stakeholder</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Role / Title</Label>
                  <Input value={form.role} onChange={updateForm('role')} placeholder="PI, Dean, Community Lead" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Sector / Affiliation</Label>
                  <Select value={form.sector} onValueChange={(value) => setForm((prev) => ({ ...prev, sector: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {['University', 'Community', 'Government', 'NGO', 'Industry', 'Donor', 'Internal'].map((sector) => (
                        <SelectItem key={sector} value={sector}>
                          {sector}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location / Community</Label>
                  <Input value={form.location} onChange={updateForm('location')} placeholder="County, region, community" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Interest</Label>
                  <Select value={form.interest} onValueChange={(value) => setForm((prev) => ({ ...prev, interest: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select interest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Influence</Label>
                  <Select value={form.influence} onValueChange={(value) => setForm((prev) => ({ ...prev, influence: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select influence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Engagement Level</Label>
                  <Select
                    value={form.engagementLevel}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, engagementLevel: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select engagement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inform">Inform</SelectItem>
                      <SelectItem value="consult">Consult</SelectItem>
                      <SelectItem value="involve">Involve</SelectItem>
                      <SelectItem value="collaborate">Collaborate</SelectItem>
                      <SelectItem value="empower">Empower</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Preferred Contact</Label>
                  <Input
                    value={form.preferredContact}
                    onChange={updateForm('preferredContact')}
                    placeholder="Email, phone, meetings"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes / Expectations</Label>
                <Textarea
                  value={form.notes}
                  onChange={updateForm('notes')}
                  placeholder="Key concerns, expectations, or contributions"
                  rows={3}
                />
              </div>
              <Button className="w-full" onClick={handleSaveStakeholder} disabled={saving || !form.name.trim()}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Save Stakeholder'}
              </Button>
            </div>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : normalizedStakeholders.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        {byType.map(([type, count]) => (
          <Card key={type} className="border border-border"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-foreground">{count}</p><p className="text-xs text-muted-foreground font-medium mt-1 capitalize">{type}</p></CardContent></Card>
        ))}
      </div>

      {/* Power-Interest Matrix Summary */}
      <Card className="border border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Stakeholder Influence-Interest Matrix</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <p className="text-xs font-semibold text-destructive mb-2">High Influence / High Interest - Manage Closely</p>
              <div className="flex flex-wrap gap-1.5">
                {normalizedStakeholders.filter(s => s.influence === 'high' && s.interest === 'high').map(s => (
                  <Badge key={s.id} variant="outline" className="text-[10px] text-foreground">{s.name}</Badge>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-warning/20 bg-warning/5 p-4">
              <p className="text-xs font-semibold text-warning mb-2">High Influence / Low-Med Interest - Keep Satisfied</p>
              <div className="flex flex-wrap gap-1.5">
                {normalizedStakeholders.filter(s => s.influence === 'high' && s.interest !== 'high').map(s => (
                  <Badge key={s.id} variant="outline" className="text-[10px] text-foreground">{s.name}</Badge>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <p className="text-xs font-semibold text-primary mb-2">Low-Med Influence / High Interest - Keep Informed</p>
              <div className="flex flex-wrap gap-1.5">
                {normalizedStakeholders.filter(s => s.influence !== 'high' && s.interest === 'high').map(s => (
                  <Badge key={s.id} variant="outline" className="text-[10px] text-foreground">{s.name}</Badge>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Low Influence / Low Interest - Monitor</p>
              <div className="flex flex-wrap gap-1.5">
                {normalizedStakeholders.filter(s => s.influence !== 'high' && s.interest !== 'high').map(s => (
                  <Badge key={s.id} variant="outline" className="text-[10px] text-foreground">{s.name}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search stakeholders..." className="pl-10 h-9" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="internal">Internal</SelectItem><SelectItem value="external">External</SelectItem><SelectItem value="donor">Donor</SelectItem><SelectItem value="government">Government</SelectItem><SelectItem value="community">Community</SelectItem></SelectContent></Select>
      </div>

      {/* Stakeholder Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {filtered.map(sh => (
          <Card key={sh.id} className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Avatar className="size-10">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">{sh.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{sh.name}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="size-3" />{sh.organization}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{sh.role}</p>
                    {sh.sector && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">{sh.sector}{sh.location ? ` • ${sh.location}` : ''}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn('text-[10px]', typeConfig[sh.type]?.color)}>{typeConfig[sh.type]?.label}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="size-7">
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit ? (
                        <DropdownMenuItem onClick={() => openForEdit(sh)}>Edit</DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem>View Engagement Log</DropdownMenuItem>
                      <DropdownMenuItem>Send Email</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3">
                {sh.email && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Mail className="size-3" />{sh.email}</span>}
                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Phone className="size-3" />{sh.phone}</span>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-1.5"><span className="text-[10px] text-muted-foreground">Influence:</span><div className={cn('h-2 rounded-full', influenceColor[sh.influence])} style={{ width: sh.influence === 'high' ? 48 : sh.influence === 'medium' ? 32 : 16 }} /></div>
                <div className="flex items-center gap-1.5"><span className="text-[10px] text-muted-foreground">Interest:</span><div className={cn('h-2 rounded-full', interestColor[sh.interest])} style={{ width: sh.interest === 'high' ? 48 : sh.interest === 'medium' ? 32 : 16 }} /></div>
              </div>
              {sh.engagementLevel && (
                <p className="text-[10px] text-muted-foreground mt-2">
                  Engagement: {String(sh.engagementLevel).replace('-', ' ')}
                </p>
              )}
              {sh.projectIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {sh.projectIds.map((pid) => {
                    const project = projectMap[pid]
                    if (!project) return null
                    return (
                      <Badge key={pid} variant="outline" className="text-[10px] text-foreground">
                        {project.code || project.shortCode || project.title}
                      </Badge>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
